import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Skeleton,
  SkeletonRows,
  SkeletonStatTile,
  SkeletonText,
  StatTile,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TabPanel,
  Table,
  Tabs,
  Timeline,
  TimelineDivider,
  TimelineItem,
  type Tone,
} from '../components/ui/index.ts';
import { PipelineCard, type PipelineCardData } from '../components/pipeline/PipelineCard.tsx';
import { PipelineColumn } from '../components/pipeline/PipelineColumn.tsx';
import { PlusIcon, SearchIcon, SparkIcon } from '../components/icons.tsx';
import { duration, money, shortDate } from '../lib/format.ts';

/**
 * Component showcase. Every example below uses the same components the real
 * pages will, so anything that looks wrong here is wrong everywhere.
 *
 * The data is invented. Section 2 has not run yet, so nothing on this page
 * touches the API.
 */
export default function Style() {
  const [tab, setTab] = useState('calls');

  return (
    <div className="space-y-12 pb-20">
      <header>
        <h1 className="text-xl font-semibold tracking-tighter">Design system</h1>
        <p className="mt-1 text-sm text-fg-subtle">
          Tokens live in{' '}
          <code className="rounded-sm bg-surface-inset px-1 py-0.5 text-xs text-fg-muted">
            web/src/styles/theme.css
          </code>
          . Components reference tokens only, never raw values.
        </p>
      </header>

      <Section title="Surfaces" description="Depth comes from lightness steps, not outlines.">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            ['canvas', 'bg-canvas'],
            ['inset', 'bg-surface-inset'],
            ['surface', 'bg-surface'],
            ['raised', 'bg-surface-raised'],
            ['overlay', 'bg-surface-overlay'],
          ].map(([name, className]) => (
            <div key={name} className="rounded-md border border-border p-1">
              <div className={`h-14 rounded-sm ${className}`} />
              <p className="mt-1.5 px-1 pb-0.5 text-2xs text-fg-subtle">{name}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Colour" description="One accent. Status tones stay muted.">
        <div className="space-y-4">
          <Swatches
            label="Accent — primary actions and active state only"
            items={[
              ['accent', 'bg-accent'],
              ['hover', 'bg-accent-hover'],
              ['active', 'bg-accent-active'],
              ['subtle', 'bg-accent-subtle'],
              ['text', 'bg-accent-text'],
            ]}
          />
          <Swatches
            label="Status"
            items={[
              ['won', 'bg-won'],
              ['lost', 'bg-lost'],
              ['at risk', 'bg-risk'],
              ['stalled', 'bg-stalled'],
              ['open', 'bg-open'],
              ['neutral', 'bg-neutral'],
            ]}
          />
          <Swatches
            label="Text"
            items={[
              ['fg', 'bg-fg'],
              ['muted', 'bg-fg-muted'],
              ['subtle', 'bg-fg-subtle'],
              ['faint', 'bg-fg-faint'],
            ]}
          />
        </div>
      </Section>

      <Section title="Typography" description="Inter, tight tracking on headings, tabular figures.">
        <Card>
          <div className="space-y-3">
            <p className="text-2xl font-semibold tracking-tighter">Sales cycle length</p>
            <p className="text-xl font-semibold tracking-tighter">Pipeline by stage</p>
            <p className="text-lg font-medium tracking-tight">Rana Ahmed</p>
            <p className="text-base text-fg">
              Body copy at 14px. Dense by default, because this is a tool someone reads all day.
            </p>
            <p className="text-sm text-fg-muted">Table body at 13px, muted.</p>
            <p className="text-xs text-fg-subtle">Metadata at 12px.</p>
            <p className="text-2xs text-fg-faint">Labels at 11px.</p>
            <div className="border-t border-border-subtle pt-3">
              <p className="mb-1 text-2xs text-fg-faint">
                Tabular figures — every digit occupies the same width
              </p>
              <p className="numeric text-lg">111,111 · 000,000 · 189,432 · $6,000</p>
              <p className="mt-1 text-lg">111,111 · 000,000 · 189,432 · $6,000</p>
              <p className="mt-1 text-2xs text-fg-faint">
                The second line is proportional. Watch the 1s.
              </p>
            </div>
          </div>
        </Card>
      </Section>

      <Section title="Buttons">
        <Card>
          <div className="space-y-4">
            <Row label="Variants">
              <Button variant="primary">Log a call</Button>
              <Button variant="secondary">Edit</Button>
              <Button variant="ghost">Cancel</Button>
              <Button variant="danger">Mark lost</Button>
            </Row>
            <Row label="With icons">
              <Button variant="primary" leading={<PlusIcon size={14} />}>
                New lead
              </Button>
              <Button variant="secondary" leading={<SearchIcon size={14} />}>
                Search transcripts
              </Button>
              <Button variant="ghost" leading={<SparkIcon size={14} />}>
                Re-run analysis
              </Button>
            </Row>
            <Row label="Sizes">
              <Button size="sm" variant="primary">
                Small
              </Button>
              <Button size="md" variant="primary">
                Medium
              </Button>
            </Row>
            <Row label="States">
              <Button variant="primary" loading>
                Saving
              </Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
              <Button variant="secondary" disabled>
                Disabled
              </Button>
            </Row>
          </div>
        </Card>
      </Section>

      <Section title="Badges" description="Semantic tones, never raw colours at the call site.">
        <Card>
          <div className="space-y-4">
            <Row label="Tones">
              {(['won', 'lost', 'risk', 'stalled', 'open', 'neutral', 'accent'] as Tone[]).map(
                (tone) => (
                  <Badge key={tone} tone={tone}>
                    {tone}
                  </Badge>
                ),
              )}
            </Row>
            <Row label="With dot">
              <Badge tone="won" dot>
                Closed won
              </Badge>
              <Badge tone="open" dot>
                With Adam
              </Badge>
              <Badge tone="risk" dot>
                No next event
              </Badge>
              <Badge tone="stalled" dot>
                21 days in stage
              </Badge>
            </Row>
          </div>
        </Card>
      </Section>

      <Section
        title="Stat tiles"
        description="Value large, label muted, delta only when a prior period exists."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Collected"
            value={money(184500)}
            delta={{ value: 0.184, period: 'vs prior 30d' }}
            hint="9 deals"
          />
          <StatTile
            label="Show rate"
            value="72%"
            delta={{ value: -0.043, period: 'vs prior 30d' }}
            hint="38 booked"
          />
          <StatTile
            label="Calls before DQ"
            value="1.4"
            delta={{ value: -0.22, period: 'vs prior 30d' }}
            higherIsBetter={false}
            hint="lower is better"
          />
          <StatTile label="Active clients" value="12" hint="no prior period" />
        </div>
        <p className="mt-3 text-xs text-fg-subtle">
          The third tile falls the same way the second does, but reads green: it passes{' '}
          <code className="text-fg-muted">higherIsBetter=false</code>, because disqualifying faster
          is good. The fourth has no delta at all rather than a fabricated zero.
        </p>
      </Section>

      <Section title="Tables" description="Dense rows, right-aligned tabular numerics.">
        <Card flush>
          <Table>
            <THead>
              <TH>Lead</TH>
              <TH>Stage</TH>
              <TH numeric>Value</TH>
              <TH numeric>Duration</TH>
              <TH numeric>Last call</TH>
            </THead>
            <TBody>
              {SAMPLE_ROWS.map((row) => (
                <TR key={row.name} interactive>
                  <TD>
                    <span className="font-medium">{row.name}</span>
                    <span className="ml-2 text-fg-subtle">{row.practice}</span>
                  </TD>
                  <TD>
                    <Badge tone={row.tone} dot>
                      {row.stage}
                    </Badge>
                  </TD>
                  <TD numeric>{money(row.value)}</TD>
                  <TD numeric muted>
                    {duration(row.seconds)}
                  </TD>
                  <TD numeric muted>
                    {shortDate(row.lastCall)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      </Section>

      <Section title="Tabs">
        <Card>
          <Tabs
            items={[
              { id: 'calls', label: 'Calls', count: 3 },
              { id: 'touches', label: 'Touches', count: 11 },
              { id: 'deals', label: 'Deals', count: 1 },
              { id: 'transcript', label: 'Transcript' },
            ]}
            value={tab}
            onChange={setTab}
            action={
              <Button size="sm" variant="ghost" leading={<SearchIcon size={13} />}>
                Search
              </Button>
            }
          />
          <TabPanel>
            <p className="text-sm text-fg-muted">
              Panel for <span className="text-fg">{tab}</span>. Arrow keys move between tabs.
            </p>
          </TabPanel>
        </Card>
      </Section>

      <Section
        title="Timeline"
        description="The lead detail spine. Calls, touches, stage changes and payments in one column."
      >
        <Card>
          <Timeline>
            <TimelineDivider label="March 2025" />
            <TimelineItem
              kind="payment"
              title="First payment received"
              timestamp="Mar 12"
              meta="$2,000 · card · mastermind"
            />
            <TimelineItem
              kind="stage"
              title="Contract signed"
              timestamp="Mar 9"
              meta="Moved from verbal yes"
            />
            <TimelineItem
              kind="call"
              title="Assessment review with Adam"
              timestamp="Mar 4, 2:15 PM"
              meta="41m · talk ratio 38%"
            >
              <div className="rounded-md border border-border-subtle bg-surface-inset p-2.5">
                <p className="text-xs leading-relaxed text-fg-muted">
                  Confirmed the database of 4,200 patients is the near-term lever. Wife joined for
                  the last 10 minutes and asked about the payment plan.
                </p>
                <div className="mt-2 flex gap-1.5">
                  <Badge tone="accent">urgency 8</Badge>
                  <Badge tone="won">affordability strong</Badge>
                </div>
              </div>
            </TimelineItem>
            <TimelineItem
              kind="touch"
              title="Follow-up text sent"
              timestamp="Mar 2"
              meta="Outbound · replied in 3h"
            />
            <TimelineItem
              kind="email"
              title="Assessment sent"
              timestamp="Mar 1"
              meta="Outbound · no reply yet"
            />
            <TimelineDivider label="February 2025" />
            <TimelineItem
              kind="call"
              title="Discovery call"
              timestamp="Feb 26, 9:00 AM"
              meta="52m · talk ratio 51%"
            />
            <TimelineItem
              kind="stage"
              title="Lead created"
              timestamp="Feb 24"
              meta="Source: webinar"
              tone="neutral"
            />
          </Timeline>
        </Card>
      </Section>

      <Section
        title="Pipeline board"
        description="Columns by stage. Drag to move is wired in section 3; these are static."
      >
        <div className="flex gap-3 overflow-x-auto pb-2">
          <PipelineColumn title="Discovery ran" count={2} totalValue="18000">
            {BOARD.discovery.map((card) => (
              <PipelineCard key={card.id} data={card} />
            ))}
          </PipelineColumn>
          <PipelineColumn title="Assessment sent" count={2} totalValue="41000">
            {BOARD.assessment.map((card) => (
              <PipelineCard key={card.id} data={card} />
            ))}
          </PipelineColumn>
          <PipelineColumn title="With Adam" count={1} totalValue="30000" dropActive>
            {BOARD.withAdam.map((card) => (
              <PipelineCard key={card.id} data={card} />
            ))}
          </PipelineColumn>
          <PipelineColumn title="Verbal yes" count={0} totalValue={null}>
            <p className="px-1 py-6 text-center text-xs text-fg-faint">Nothing here yet</p>
          </PipelineColumn>
        </div>
        <p className="mt-3 text-xs text-fg-subtle">
          The third column shows the drop-target state. Cards flag a missing next event, which is
          the leak the spreadsheet could never surface.
        </p>
      </Section>

      <Section title="Empty states" description="Always say what to do next.">
        <div className="grid gap-3 md:grid-cols-2">
          <Card flush>
            <EmptyState
              title="No calls logged yet"
              description="Fathom syncs every 15 minutes. If this call happened elsewhere, log it manually."
              action={
                <Button size="sm" variant="primary" leading={<PlusIcon size={13} />}>
                  Log a call
                </Button>
              }
            />
          </Card>
          <Card flush>
            <EmptyState
              icon={<SearchIcon size={18} />}
              title="No leads match those filters"
              description="Try clearing the source filter, or widen the date range beyond the last 30 days."
              action={
                <Button size="sm" variant="secondary">
                  Clear filters
                </Button>
              }
            />
          </Card>
        </div>
      </Section>

      <Section
        title="Loading"
        description="Skeletons matching the final layout. No spinners except inside a button you just pressed."
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SkeletonStatTile />
            <SkeletonStatTile />
            <SkeletonStatTile />
            <SkeletonStatTile />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Card flush>
              <Table>
                <THead>
                  <TH>Lead</TH>
                  <TH>Stage</TH>
                  <TH numeric>Value</TH>
                  <TH numeric>Last call</TH>
                </THead>
                <TBody>
                  <SkeletonRows rows={5} columns={4} />
                </TBody>
              </Table>
            </Card>
            <Card>
              <CardHeader title="Loading text" description="Last line is short, like real copy" />
              <SkeletonText lines={4} />
              <div className="mt-4 flex gap-2">
                <Skeleton className="h-7 w-20" />
                <Skeleton className="h-7 w-20" />
              </div>
            </Card>
          </div>
        </div>
      </Section>

      <Section title="Motion" description="150ms ease-out on hover and state change. Nothing bouncy.">
        <Card>
          <div className="flex flex-wrap gap-3">
            <div className="cursor-pointer rounded-md border border-border bg-surface px-4 py-3 text-sm transition hover:border-border-strong hover:bg-surface-raised">
              Hover: surface + border
            </div>
            <div className="cursor-pointer rounded-md bg-accent px-4 py-3 text-sm text-accent-fg transition hover:bg-accent-hover">
              Hover: accent
            </div>
            <div className="cursor-pointer rounded-md border border-border px-4 py-3 text-sm text-fg-subtle transition hover:text-fg">
              Hover: text only
            </div>
          </div>
          <p className="mt-3 text-xs text-fg-subtle">
            A bare <code className="text-fg-muted">transition</code> class is already 150ms ease-out,
            because the theme sets the Tailwind defaults. Nobody has to remember the duration.
          </p>
        </Card>
      </Section>
    </div>
  );
}

/* ---------- showcase scaffolding ---------- */

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="text-lg font-medium tracking-tight text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-fg-subtle">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-24 shrink-0 text-2xs text-fg-faint">{label}</span>
      {children}
    </div>
  );
}

function Swatches({ label, items }: { label: string; items: [string, string][] }) {
  return (
    <div>
      <p className="mb-1.5 text-2xs text-fg-faint">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.map(([name, className]) => (
          <div key={name} className="w-24">
            <div className={`h-10 rounded-md border border-border ${className}`} />
            <p className="mt-1 text-2xs text-fg-subtle">{name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- invented data ---------- */

const SAMPLE_ROWS: {
  name: string;
  practice: string;
  stage: string;
  tone: Tone;
  value: number;
  seconds: number;
  lastCall: string;
}[] = [
  {
    name: 'Rana Ahmed',
    practice: 'Vitality Functional Medicine',
    stage: 'With Adam',
    tone: 'open',
    value: 30000,
    seconds: 2460,
    lastCall: '2025-03-04',
  },
  {
    name: 'Mark Sowers',
    practice: 'Sowers Family Dental',
    stage: 'Closed won',
    tone: 'won',
    value: 6000,
    seconds: 3180,
    lastCall: '2025-03-01',
  },
  {
    name: 'Susan Delgado',
    practice: 'Coastal Regenerative',
    stage: 'Assessment',
    tone: 'stalled',
    value: 11000,
    seconds: 1920,
    lastCall: '2025-02-14',
  },
  {
    name: 'Kevin Ortiz',
    practice: 'Meridian Ortho Group',
    stage: 'At risk',
    tone: 'risk',
    value: 75000,
    seconds: 3720,
    lastCall: '2025-02-11',
  },
  {
    name: 'Dana Whitfield',
    practice: 'Northside Hormone Clinic',
    stage: 'Closed lost',
    tone: 'lost',
    value: 6000,
    seconds: 1500,
    lastCall: '2025-01-29',
  },
];

const BOARD: Record<'discovery' | 'assessment' | 'withAdam', PipelineCardData[]> = {
  discovery: [
    {
      id: 1,
      fullName: 'Priya Raghunathan',
      practiceName: 'Lakeshore Wellness',
      dealValue: '6000',
      daysInStage: 2,
      risk: 'none',
      nextEvent: 'Assessment due Thu',
    },
    {
      id: 2,
      fullName: 'Tom Blackwell',
      practiceName: null,
      dealValue: '12000',
      daysInStage: 9,
      risk: 'stalled',
      nextEvent: null,
    },
  ],
  assessment: [
    {
      id: 3,
      fullName: 'Susan Delgado',
      practiceName: 'Coastal Regenerative',
      dealValue: '11000',
      daysInStage: 21,
      risk: 'at_risk',
      nextEvent: null,
    },
    {
      id: 4,
      fullName: 'Mark Sowers',
      practiceName: 'Sowers Family Dental',
      dealValue: '30000',
      daysInStage: 4,
      risk: 'none',
      nextEvent: 'Wife joining Tue call',
    },
  ],
  withAdam: [
    {
      id: 5,
      fullName: 'Rana Ahmed',
      practiceName: 'Vitality Functional Medicine',
      dealValue: '30000',
      daysInStage: 1,
      risk: 'none',
      nextEvent: 'Adam call Fri 10am',
    },
  ],
};
